/**
 * <PresentationMode /> — Mode présentation plein écran d'une fiche séance.
 *
 * Utilisable face à un groupe d'élèves (en salle) ou affiché sur écran
 * grand format. Une étape à la fois, navigation slide-by-slide, police XL.
 *
 * Props :
 *  - trameId   : 'salle' | 'conduite'
 *  - title     : titre de la séance
 *  - kicker    : libellé contexte
 *  - meta      : métadonnées (duree/niveau/lieu/materiel)
 *  - steps     : objet { [stepId]: { content, items?, notes? } }
 *  - onClose   : callback de fermeture
 *
 * Interactions :
 *  - Flèches ← / → ou Espace : navigation
 *  - Échap : fermeture
 *  - Bouton "Suivant"/"Précédent" : navigation tactile
 *  - Indicateur de progression : 3 / 9
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Clock,
  GraduationCap,
  MapPin,
  Package,
} from 'lucide-react';
import { Button } from '../ui/button';
import { getTrameById } from '../../data/teacher/canevasMeta';

function MetaPill({ icon: Icon, value, color }) {
  if (!value) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs sm:text-sm font-semibold border"
      style={{ borderColor: `${color}55`, color, background: `${color}11` }}
    >
      <Icon className="w-3.5 h-3.5" /> {value}
    </span>
  );
}

function Slide({ step, data, accent, current, total }) {
  const Icon = step.icon;
  const isEmpty = !data || (!data.content && !data.items?.length);

  return (
    <div className="flex-1 flex flex-col items-stretch overflow-y-auto" data-testid={`slide-${step.id}`}>
      {/* Slide header */}
      <header className="flex items-center gap-4 sm:gap-5 mb-4 sm:mb-6 flex-shrink-0">
        <div
          className="w-14 h-14 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center text-white font-black flex-shrink-0 text-2xl sm:text-4xl"
          style={{ background: accent }}
        >
          {step.n}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Icon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: accent }} />
            <span
              className="text-[10px] sm:text-xs font-black uppercase tracking-widest"
              style={{ color: accent }}
            >
              Étape {current} / {total}
            </span>
          </div>
          <h2
            className="text-2xl sm:text-3xl lg:text-5xl font-black tracking-tight leading-[1.05]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {step.label}
          </h2>
          <p className="text-sm sm:text-lg text-foreground/75 italic mt-1.5 sm:mt-2.5 leading-snug">
            {step.intent}
          </p>
        </div>
      </header>

      {/* Slide body */}
      <div className="flex-1 min-h-0">
        {isEmpty ? (
          <div className="border-2 border-dashed border-border rounded-2xl p-5 sm:p-10 text-center">
            <p
              className="text-base sm:text-xl text-muted-foreground italic leading-relaxed mb-4 sm:mb-6 max-w-3xl mx-auto"
            >
              {step.description}
            </p>
            {step.examples?.length > 0 && (
              <>
                <div
                  className="text-[10px] sm:text-xs font-black uppercase tracking-widest mb-3"
                  style={{ color: accent }}
                >
                  Exemples
                </div>
                <ul className="space-y-2 text-base sm:text-xl max-w-3xl mx-auto text-left">
                  {step.examples.map((ex, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span style={{ color: accent }}>•</span>
                      <span>{ex}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-7">
            {data.content && (
              <div
                className="text-lg sm:text-2xl lg:text-3xl leading-relaxed text-foreground/95 max-w-5xl"
                dangerouslySetInnerHTML={{ __html: data.content }}
              />
            )}
            {data.items?.length > 0 && (
              <ul className="space-y-3 sm:space-y-4 max-w-5xl">
                {data.items.map((it, i) => (
                  <li key={i} className="flex items-start gap-3 sm:gap-4">
                    <span
                      className="mt-2 sm:mt-3 w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0"
                      style={{ background: accent }}
                      aria-hidden="true"
                    />
                    <span
                      className="text-base sm:text-2xl lg:text-3xl leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: typeof it === 'string' ? it : '' }}
                    />
                  </li>
                ))}
              </ul>
            )}
            {data.notes && (
              <div
                className="text-sm sm:text-lg italic text-muted-foreground border-l-4 pl-4 sm:pl-5 max-w-5xl"
                style={{ borderColor: accent }}
                dangerouslySetInnerHTML={{
                  __html: `<strong class="not-italic" style="color:${accent}">Note moniteur — </strong>${data.notes}`,
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PresentationMode({
  trameId = 'conduite',
  title,
  kicker,
  meta = {},
  steps = {},
  onClose,
}) {
  const trame = getTrameById(trameId);
  const orderedSteps = trame?.steps || [];
  const total = orderedSteps.length;
  const [idx, setIdx] = useState(0);

  const goNext = useCallback(() => setIdx((i) => Math.min(i + 1, total - 1)), [total]);
  const goPrev = useCallback(() => setIdx((i) => Math.max(i - 1, 0)), []);

  // Clavier
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, onClose]);

  // Bloquer le scroll du body
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  if (!trame) return null;
  const currentStep = orderedSteps[idx];
  const accent = currentStep.color || trame.accent;

  return (
    <div
      className="fixed inset-0 z-[100] bg-background flex flex-col"
      data-testid="presentation-mode-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Mode présentation : ${title}`}
    >
      {/* Top bar */}
      <header
        className="flex items-center gap-2 sm:gap-4 px-3 sm:px-6 py-2.5 sm:py-3 border-b border-border bg-card/90 backdrop-blur flex-shrink-0"
        style={{ borderTop: `4px solid ${accent}` }}
      >
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] sm:text-xs font-black uppercase tracking-widest"
            style={{ color: accent }}
          >
            {kicker || trame.label}
          </div>
          <div className="text-sm sm:text-base font-black truncate">{title}</div>
        </div>
        {/* Méta visible desktop only */}
        <div className="hidden lg:flex flex-wrap gap-1.5">
          <MetaPill icon={Clock} value={meta.duree} color={accent} />
          <MetaPill icon={GraduationCap} value={meta.niveau} color={accent} />
          <MetaPill icon={MapPin} value={meta.lieu} color={accent} />
          <MetaPill icon={Package} value={meta.materiel} color={accent} />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onClose}
          className="gap-1.5 flex-shrink-0"
          data-testid="presentation-close"
        >
          <X className="w-4 h-4" /> <span className="hidden sm:inline">Quitter</span>
        </Button>
      </header>

      {/* Slide */}
      <main className="flex-1 overflow-hidden flex flex-col p-4 sm:p-10 lg:p-14">
        <Slide step={currentStep} data={steps[currentStep.id]} accent={accent} current={idx + 1} total={total} />
      </main>

      {/* Bottom controls */}
      <footer className="flex items-center justify-between gap-2 px-3 sm:px-6 py-3 border-t border-border bg-card/90 backdrop-blur flex-shrink-0">
        <Button
          variant="outline"
          size="sm"
          onClick={goPrev}
          disabled={idx === 0}
          className="gap-1.5"
          data-testid="presentation-prev"
        >
          <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">Précédent</span>
        </Button>

        {/* Progression */}
        <div className="flex-1 mx-2 sm:mx-4">
          <div className="flex items-center gap-1.5 justify-center" role="tablist" aria-label="Progression slide">
            {orderedSteps.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Aller à l'étape ${i + 1} : ${s.label}`}
                aria-current={i === idx ? 'step' : undefined}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? 'w-6 sm:w-8' : 'w-1.5 sm:w-2 hover:w-3'
                }`}
                style={{
                  background: i === idx ? accent : i < idx ? `${accent}99` : 'hsl(var(--border))',
                }}
                data-testid={`presentation-dot-${i}`}
              />
            ))}
          </div>
          <div className="text-center text-[10px] sm:text-xs text-muted-foreground mt-1 tabular-nums">
            {idx + 1} / {total}
          </div>
        </div>

        <Button
          size="sm"
          onClick={goNext}
          disabled={idx === total - 1}
          className="gap-1.5"
          style={{ background: accent, color: 'white' }}
          data-testid="presentation-next"
        >
          <span className="hidden sm:inline">Suivant</span> <ChevronRight className="w-4 h-4" />
        </Button>
      </footer>
    </div>
  );
}
